import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const DocumentProgressContext = createContext(null);

export const DocumentProgressProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [minimized, setMinimized] = useState(false);

  // `afterId` inserts the new rows directly below an existing one instead of at
  // the end. Needed when a row turns out to stand for more than one document —
  // a Create Document batch can discover mid-run that a client produced both a
  // regular and a reimbursement invoice — so the extra rows sit beside their
  // sibling rather than after every client that has not started yet.
  const addItems = useCallback((newItems, { afterId } = {}) => {
    setItems(prev => {
      const incoming = new Map(newItems.map(n => [n.id, n]));
      // Replace existing items with the same id in-place, append truly new ones.
      const updated = prev.map(p => incoming.has(p.id) ? { ...p, ...incoming.get(p.id) } : p);
      const existing = new Set(prev.map(p => p.id));
      const brandNew = newItems.filter(n => !existing.has(n.id));
      if (brandNew.length === 0) return updated;
      const at = afterId == null ? -1 : updated.findIndex(p => p.id === afterId);
      if (at === -1) return [...updated, ...brandNew];
      return [...updated.slice(0, at + 1), ...brandNew, ...updated.slice(at + 1)];
    });
    setMinimized(false);
  }, []);

  const updateItem = useCallback((id, updates) => {
    setItems(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p));
  }, []);

  const removeItems = useCallback((ids) => {
    const drop = new Set(ids);
    setItems(prev => prev.filter(p => !drop.has(p.id)));
  }, []);

  // Ids whose retry has been started but whose row has not re-rendered yet, so
  // a double click cannot fire the same request twice.
  const retrying = useRef(new Set());

  // "Try again" on a failed row. A producer opts a row in by putting a `retry`
  // function on it when it fails; the function re-runs that one row's work and
  // settles the row itself, exactly as the original run did. The row is flipped
  // back to `generating` here first so the button disappears on the click that
  // pressed it — a second click cannot start a second request. `retry` is
  // cleared with it: a row that fails again gets a fresh one from its producer.
  // The function is a closure over the page that made the row, so it keeps
  // working after the user has navigated away (the setters of an unmounted page
  // are no-ops; the panel update goes through this provider, which lives in App).
  const retryItem = useCallback((id) => {
    const row = items.find(p => p.id === id);
    if (!row || row.status !== 'error' || typeof row.retry !== 'function') return;
    if (retrying.current.has(id)) return;
    retrying.current.add(id);
    const fn = row.retry;
    setItems(prev => prev.map(p => p.id === id
      ? { ...p, status: 'generating', error: null, note: null, retry: undefined } : p));
    Promise.resolve()
      .then(fn)
      .catch(e => {
        // Producers settle their own rows and never throw; this is only a net
        // for one that does, so the row cannot spin forever.
        console.error('Retry failed', e);
        setItems(prev => prev.map(p => p.id === id && p.status === 'generating'
          ? { ...p, status: 'error', error: e?.message || 'Retry failed' } : p));
      })
      .finally(() => retrying.current.delete(id));
  }, [items]);

  const clearItems = useCallback(() => setItems([]), []);

  return (
    <DocumentProgressContext.Provider value={{ items, minimized, setMinimized, addItems, updateItem, removeItems, retryItem, clearItems }}>
      {children}
    </DocumentProgressContext.Provider>
  );
};

export const useDocumentProgress = () => useContext(DocumentProgressContext);
